/**
 * Creates a *debounced* function that delays calling `fn` until
 * `wait` milliseconds have passed since the last call.
 *
 * @example
 * ```ts
 * const debouncedSearch = debounce((query: string) => {
 * console.log('Searching for:', query);
 * }, 300);
 *
 * debouncedSearch('a');
 * debouncedSearch('b'); // Cancels the previous one, schedules a new one
 * // After 300ms of inactivity: logs "Searching for: b"
 * ```
 *
 * @param fn - The function we want to debounce
 * @param wait - The number of milliseconds to delay the call (default: 300ms)
 * @returns Debounced function with a `cancel()` method to cancel the pending call.
 */
export function debounce<T extends (...args: never[]) => unknown>(
  fn: T,
  wait: number = 300
): T & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debounced = function (this: unknown, ...args: Parameters<T>) {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      fn.apply(this, args);
      timeoutId = null;
    }, wait);
  } as T & { cancel: () => void };

  debounced.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced;
}
